import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { ProfileSkeleton } from "@/components/Skeleton";
import { Person } from "@/types";
import { renderName } from "@/lib/personHelpers";
import { useRecentlyVisited } from "@/hooks/useRecentlyVisited";
import {
  getProgramName,
  getPrimaryFacilityName,
  getPrimaryEmploymentTitle,
  getPrimarySectionName,
  getPrimaryFacilityLocation,
  getPrimaryJobClassification
} from "@/pages/people/_utils";
import {
  PERSON_DETAILS_HEADER_MEMBERSHIP_QUERY,
  PERSON_DETAILS_HEADER_CAREER_QUERY,
  PERSON_DETAILS_HEADER_QUERY
} from "@/features/person/queries";
import ObjectHeading from "@/components/ObjectHeading";
import { useCanViewPersonCareerBool, useCanViewPersonMembershipBool } from "@/hooks/useCapabilities";

export interface MobilePersonOverviewProps {
  pgaId: string;
}

type PersonData = { person: Person };

function mergeHeaderPerson(headerPerson: Person, membershipPerson?: Person, careerPerson?: Person): Person {
  return {
    ...headerPerson,
    ...(membershipPerson
      ? {
          latestProgram: membershipPerson.latestProgram,
          ccmsSuspensions: membershipPerson.ccmsSuspensions
        }
      : {}),
    ...(careerPerson?.employments !== undefined ? { employments: careerPerson.employments } : {})
  };
}

export default function MobilePersonOverview({ pgaId }: MobilePersonOverviewProps) {
  const canViewPersonMembership = useCanViewPersonMembershipBool();
  const canViewPersonCareer = useCanViewPersonCareerBool();

  const { data, loading, error } = useQuery<PersonData>(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId }
  });

  const { data: membershipData, loading: membershipLoading } = useQuery<PersonData>(
    PERSON_DETAILS_HEADER_MEMBERSHIP_QUERY,
    {
      variables: { pgaId },
      skip: !canViewPersonMembership,
      fetchPolicy: "network-only"
    }
  );

  const { data: careerData, loading: careerLoading } = useQuery<PersonData>(PERSON_DETAILS_HEADER_CAREER_QUERY, {
    variables: { pgaId },
    skip: !canViewPersonCareer,
    fetchPolicy: "network-only"
  });

  const { addRecentlyVisitedItem } = useRecentlyVisited({ skipListQuery: true });
  const membershipChromePending = canViewPersonMembership && membershipLoading;
  const careerChromePending = canViewPersonCareer && careerLoading;

  useEffect(() => {
    const headerPerson = data?.person;
    if (!headerPerson) return;
    if (membershipChromePending || careerChromePending) return;

    const person = mergeHeaderPerson(headerPerson, membershipData?.person, careerData?.person);

    addRecentlyVisitedItem({ type: "people", entityId: person.id });
  }, [
    addRecentlyVisitedItem,
    careerChromePending,
    careerData?.person,
    data?.person,
    membershipChromePending,
    membershipData?.person
  ]);

  if (loading || membershipChromePending || careerChromePending) return <ProfileSkeleton />;
  if (error) return <div>Error loading person details</div>;
  if (!data?.person) return <div>Person not found</div>;

  const person = mergeHeaderPerson(data.person, membershipData?.person, careerData?.person);

  const { profilePhoto, displayName, informalName, preferredLastName, suffixName, middleName, firstName } = person;

  const personName = renderName(displayName, informalName, preferredLastName, suffixName, middleName, firstName);
  const memberType = getProgramName(person);
  const sectionName = getPrimarySectionName(person);
  const facility = canViewPersonCareer ? getPrimaryFacilityName(person) : undefined;
  const facilityLocation = canViewPersonCareer ? getPrimaryFacilityLocation(person) : undefined;
  const jobTitle = canViewPersonCareer ? getPrimaryEmploymentTitle(person) : undefined;
  const jobClassification = canViewPersonCareer ? getPrimaryJobClassification(person) : undefined;

  // Combine program type and section for display
  const programAndSection = [memberType, sectionName].filter(Boolean).join(", ");

  // Combine facility name with location
  const facilityWithLocation = [facility, facilityLocation].filter(Boolean).join(", ");

  // Combine job title with classification
  const jobWithClassification = jobTitle && jobClassification ? `${jobTitle} (${jobClassification})` : jobTitle;

  return (
    <div
      className="bg-sys-brand-surface pb-4 border-b border-sys-brand-outline-variant"
      data-testid="mobile-person-overview"
    >
      <ObjectHeading
        title={personName}
        imageUrl={profilePhoto}
        iconName="person"
        variant="landing-page"
        metaData={[
          ...(programAndSection ? [{ text: programAndSection, boldValue: true }] : []),
          { text: pgaId, label: "PGA ID", copyable: true },
          ...(facilityWithLocation ? [{ text: facilityWithLocation, label: "Facility" }] : []),
          ...(jobWithClassification ? [{ text: jobWithClassification, label: "Job" }] : [])
        ]}
      />
    </div>
  );
}
